import React, { useState, useEffect } from "react";
import { useAuth } from "./utils/authContext";
import { useFeatureFlags } from "./contexts/FeatureFlagContext";
import OnboardingFlow from "./utils/OnboardingFlow";
import ClinicalDayView from "./components/dashboard/ClinicalDayView";
import { useNavigate } from "react-router-dom";
import { isLocalOnboardingPreviewEnabled } from "./utils/onboardingPreview";
import Modal from "@mui/material/Modal";
import { startDashboardTiming } from './utils/dashboardPerformance';

const DashboardOnboardingOverlay = ({ children, loading = false }) => (
  <Modal
    open
    disableEscapeKeyDown
    className="dashboard-onboarding-modal"
  >
    <div
      className="dashboard-onboarding"
      role={loading ? "status" : "dialog"}
      aria-live={loading ? "polite" : undefined}
      aria-modal={loading ? undefined : "true"}
      aria-label={loading ? "Checking account setup" : "Account onboarding"}
      tabIndex={-1}
    >
      {children}
    </div>
  </Modal>
);

const Dashboard = () => {
  const { currentUser, accountData, refreshAccount } = useAuth();
  const [timing] = useState(() => startDashboardTiming({ userId: currentUser?.uid }));
  const [completionError, setCompletionError] = useState(null);
  const { flags = {} } = useFeatureFlags();
  const navigate = useNavigate();
  const onboardingPreview = isLocalOnboardingPreviewEnabled();
  const isSettingUp = !onboardingPreview && (!currentUser || !accountData);
  // Legacy records omit these flags; new/incomplete accounts use explicit false.
  const needsOnboarding = accountData?.hasAcceptedTerms === false || accountData?.hasClonedData === false;
  const [onboardingUserId, setOnboardingUserId] = useState(null);

  // A background account refresh must not dismiss the completion screen before
  // the user chooses whether to explore their sample patient.
  useEffect(() => {
    if (needsOnboarding) setOnboardingUserId(currentUser?.uid);
  }, [needsOnboarding, currentUser?.uid]);

  useEffect(() => {
    if (!isSettingUp) timing.stage('accountReady');
  }, [isSettingUp, timing]);

  if (isSettingUp) {
    return (
      <DashboardOnboardingOverlay loading>
        <div className="account-setup-container">
          <div className="account-setup-title">Loading...</div>
          <div className="account-setup-progress">
            <div className="account-setup-progress-bar"></div>
          </div>
        </div>
      </DashboardOnboardingOverlay>
    );
  }

  if (needsOnboarding || onboardingPreview || (currentUser?.uid && onboardingUserId === currentUser.uid)) {
    return (
      <DashboardOnboardingOverlay>
        {completionError && <p role="alert">{completionError}</p>}
        <OnboardingFlow
          user={currentUser}
          previewMode={onboardingPreview}
          aiAssistantAvailable={Boolean(flags.aiAssistant)}
          commentsAvailable={Boolean(flags.documentComments)}
          hasAcceptedTerms={accountData?.hasAcceptedTerms === true}
          hasClonedData={accountData?.hasClonedData}
          userSettings={accountData?.settings || null}
          onExitPreview={() => {
            navigate('/dashboard', { replace: true });
          }}
          onComplete={async (destination) => {
            setCompletionError(null);
            if (!onboardingPreview) {
              try {
                const updatedAccount = await refreshAccount();
                if (!updatedAccount || updatedAccount.hasAcceptedTerms === false || updatedAccount.hasClonedData === false) {
                  setCompletionError('Account setup is not complete yet. Please try again.');
                  return;
                }
              } catch {
                setCompletionError('Could not refresh account setup. Please try again.');
                return;
              }
              setOnboardingUserId(null);
            }
            if (destination?.exploreSample) {
              navigate(destination.patientId ? '/patient' : '/patients', {
                replace: true,
                ...(destination.patientId ? { state: { patientId: destination.patientId, from: '/dashboard' } } : {}),
              });
              return;
            }
            if (onboardingPreview) {
              navigate('/dashboard', { replace: true });
              return;
            }
            window.location.reload();
          }}
        />
      </DashboardOnboardingOverlay>
    );
  }

  return <ClinicalDayView timing={timing} />;
};

export default Dashboard;
